/* RouteBuilderDialog — the single route-planner UI used by both Open
 * Houses (`RouteDialog`) and Brokerages (`BrokerageRouteDialog`). Callers
 * only supply normalized stops, copy, and the backend URL builder. */
import React, { useEffect, useState } from "react";
import { Route, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import useRouteOptimizer from "@/hooks/useRouteOptimizer";
import StartPointPicker from "./StartPointPicker";
import StopsList from "./StopsList";
import RouteResult from "./RouteResult";
import { OptimizationBadge, ReoptimizeButton, OptimizationFailureBanner } from "./OptimizationStatus";

const TRAVEL_MODES = [
  { value: "driving", label: "Driving" },
  { value: "walking", label: "Walking" },
  { value: "bicycling", label: "Cycling" },
  { value: "transit", label: "Transit" },
];

export default function RouteBuilderDialog({
  open,
  onClose,
  stops, // [{id, title, subtitle, lat, lng}] in current order
  onReorder,
  onRemoveStop,
  optimizationState, // 'none' | 'optimized' | 'manual'
  onApplyOptimized, // (orderedIds, directions) => void
  homeCoords,
  title,
  unitLabel, // "stop" | "office"
  buildRequest, // async (startAddress, travelMode) => route payload
  testIdPrefix: p,
  page,
  renderResultExtras, // optional (result) => node
  onStopClick, // optional (stopId) => void — makes stop titles clickable
}) {
  const { user } = useAuth();
  const homeAddress = (user?.home_address || "").trim();
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const opt = useRouteOptimizer({
    open,
    stops,
    optimizationState,
    onApplyOptimized,
    homeAddress,
    homeCoords,
    page,
  });

  useEffect(() => {
    if (!open) setResult(null);
  }, [open]);
  // Any change to the stop order invalidates a built URL set.
  const orderKey = stops.map((s) => s.id).join(",");
  useEffect(() => {
    setResult(null);
  }, [orderKey, opt.travelMode]);

  const buildRoute = async () => {
    setBusy(true);
    try {
      setResult(await buildRequest(opt.startAddressForApi(), opt.travelMode));
    } catch (e) {
      toast.error("Route build failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const n = stops.length;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="w-[calc(100vw-1rem)] max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6"
        data-testid={`${p}-dialog`}
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Route className="h-5 w-5 text-slate-500" /> {title}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            {n} {unitLabel}{n === 1 ? "" : "s"}. Google Maps optimizes the order as a one-way trip from your start
            point (ending at the farthest {unitLabel}); we then chunk it into 10-stop legs for export.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 min-w-0">
          <StartPointPicker opt={opt} homeAddress={homeAddress} testIdPrefix={p} />

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Travel mode</Label>
            <Select value={opt.travelMode} onValueChange={opt.setTravelMode}>
              <SelectTrigger className="min-h-[44px] mt-1" data-testid={`${p}-travel-mode`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRAVEL_MODES.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-xs uppercase tracking-wider text-slate-500">Stops (in order)</div>
                <OptimizationBadge opt={opt} optimizationState={optimizationState} stopCount={n} testIdPrefix={p} />
              </div>
              <div className="flex items-center gap-2">
                <ReoptimizeButton opt={opt} testIdPrefix={p} />
                <div className="text-[10px] text-slate-400 hidden sm:block">Use ↑↓ to re-order</div>
              </div>
            </div>
            <OptimizationFailureBanner opt={opt} testIdPrefix={p} />
            <StopsList stops={stops} onReorder={onReorder} onRemoveStop={onRemoveStop} onStopClick={onStopClick} testIdPrefix={p} />
          </div>

          <RouteResult result={result} testIdPrefix={p}>
            {renderResultExtras ? renderResultExtras(result) : null}
          </RouteResult>
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button variant="ghost" onClick={onClose} className="min-h-[44px]" data-testid={`${p}-close-button`}>
            Close
          </Button>
          {!result ? (
            <Button
              onClick={buildRoute}
              disabled={busy || n === 0 || !opt.startReady}
              className="text-white min-h-[44px]"
              style={{ background: "hsl(var(--brand))" }}
              data-testid={`${p}-build-confirm-button`}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-2" />}
              Build Route
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
